import { DialogComponent } from '@theme/dialog';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent } from '@shopify/events';

/**
 * The cart attribute the message is stored under. It surfaces on the order as
 * "Additional details → Gift message"; the same text is mirrored into the cart
 * note so it also reaches the order's Notes panel and the packing slip.
 */
const GIFT_MESSAGE_ATTRIBUTE = 'Gift message';

/**
 * A custom element that adds, edits and removes a gift message on the cart.
 *
 * The card is server-rendered from `cart.attributes`, so every cart re-render
 * (quantity changes, discounts) redraws it in the right state. This element only
 * owns the panel and the write.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The gift message panel.
 * @property {HTMLTextAreaElement} messageInput - The message field.
 * @property {HTMLElement} count - The character counter.
 * @property {HTMLElement} error - The error message.
 * @property {HTMLButtonElement} saveButton - The submit button.
 *
 * @extends {DialogComponent}
 */
class CartGiftMessage extends DialogComponent {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Opens the panel with the field focused.
   */
  openPanel = () => {
    this.#setError(false);
    this.updateCount();
    this.showDialog();
    // showDialog() opens on the next frame; focus after it.
    requestAnimationFrame(() => requestAnimationFrame(() => this.refs.messageInput?.focus()));
  };

  /**
   * Keeps the character counter in step with the field.
   */
  updateCount = () => {
    const { messageInput, count } = this.refs;
    if (!messageInput || !count) return;

    count.textContent = `${messageInput.value.length}/${messageInput.maxLength}`;
  };

  /**
   * Saves the message from the panel.
   * @param {SubmitEvent} event - The submit event on the panel form.
   */
  saveMessage = (event) => {
    event.preventDefault();
    this.#write(this.refs.messageInput?.value.trim() ?? '');
  };

  /**
   * Clears the saved message.
   */
  removeMessage = () => {
    if (this.refs.messageInput) this.refs.messageInput.value = '';
    this.#write('');
  };

  /**
   * Writes the message to the cart and re-renders the section.
   * An empty string removes both the attribute and the note.
   * @param {string} message - The message to save.
   */
  async #write(message) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    this.#setBusy(true);
    this.#setError(false);

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            attributes: { [GIFT_MESSAGE_ATTRIBUTE]: message },
            note: message,
            sections: [sectionId],
          }),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);

      const data = await response.json();

      // Close before morphing: the server-rendered panel has no [open], and
      // morphing an open modal dialog would drop it without restoring focus.
      await this.closeDialog();
      await morphSection(sectionId, data.sections[sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });

      this.#restoreFocus(sectionId, message);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      this.#setError(true);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to update gift message',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      this.#setBusy(false);
    }
  }

  /**
   * Puts focus back on the card after the re-render replaced its buttons.
   * @param {string} sectionId - The re-rendered section.
   * @param {string} message - The message just written.
   */
  #restoreFocus(sectionId, message) {
    const card = document.querySelector(`cart-gift-message[data-section-id="${sectionId}"]`);
    const target = card?.querySelector(message ? '[data-gift-edit]' : '[data-gift-add]');
    if (target instanceof HTMLElement) target.focus();
  }

  /**
   * @param {boolean} busy - Whether a write is in flight.
   */
  #setBusy(busy) {
    const { saveButton } = this.refs;
    this.toggleAttribute('aria-busy', busy);
    if (saveButton) saveButton.disabled = busy;
  }

  /**
   * @param {boolean} visible - Whether to show the error.
   */
  #setError(visible) {
    this.refs.error?.classList.toggle('hidden', !visible);
  }
}

if (!customElements.get('cart-gift-message')) {
  customElements.define('cart-gift-message', CartGiftMessage);
}
